/**
 * QuoteAuthorPage — /quotes/authors/:slug
 * Rich profile for popular people (quote authors). Includes:
 *   • Hero with Wikipedia photo + name + description
 *   • Full biography from Wikipedia (with a "Read on Wikipedia" outbound)
 *   • Curated quotes carousel
 *   • Community message board (guest book)
 *
 * Backend: `/api/quote-authors/{slug}` (existing) +
 *          `/api/quote-authors/{slug}/messages` (new).
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ExternalLink, Quote as QuoteIcon, Loader2, ArrowLeft, User,
  MessagesSquare, Send, Trash2, ShieldCheck, Sparkles, BookOpen,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

function _ago(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function QuoteAuthorPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/quote-authors/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    setMessagesLoading(true);
    axios.get(`${API}/api/quote-authors/${slug}/messages?limit=50`)
      .then((r) => setMessages(r.data.items || []))
      .catch(() => setMessages([]))
      .finally(() => setMessagesLoading(false));
  }, [slug]);

  const postMessage = async (text, done) => {
    if (!user) { toast.error("Sign in to leave a message"); return; }
    try {
      const { data: m } = await axios.post(
        `${API}/api/quote-authors/${slug}/messages`,
        { text },
        { withCredentials: true, headers: { "Content-Type": "application/json" } },
      );
      setMessages((cur) => [m, ...cur]);
      toast.success("Message posted");
      done?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post message");
    }
  };

  const deleteMessage = async (id) => {
    try {
      await axios.delete(`${API}/api/quote-authors/messages/${id}`, { withCredentials: true });
      setMessages((cur) => cur.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete"); }
  };

  if (loading) return (
    <div className="max-w-3xl mx-auto py-16 px-4 text-center opacity-60">
      <Loader2 className="w-6 h-6 mx-auto animate-spin" />
    </div>
  );
  if (!data) return (
    <div className="max-w-3xl mx-auto py-16 px-4 text-center">
      <h1 className="font-display text-3xl mb-2">Author not found</h1>
      <Link to="/" className="wofy-link">← Home</Link>
    </div>
  );

  const { author, quotes } = data;
  const heroBio = author.bio_summary || (author.bio || "").split(/(?<=\. )/)[0] || "";

  return (
    <div className="max-w-4xl mx-auto py-8 px-4" data-testid={`author-page-${author.slug}`}>
      <Link to="/quotes"
            className="inline-flex items-center gap-1 text-sm opacity-70 hover:opacity-100 mb-4">
        <ArrowLeft className="w-4 h-4" /> All quotes
      </Link>

      {/* Hero */}
      <section className="rounded-3xl overflow-hidden border mb-8"
               style={{
                 borderColor: "var(--wofy-border)",
                 background: "linear-gradient(135deg,#FEF9F0 0%,#F5F3FF 100%)",
               }}
               data-testid="author-hero">
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-0">
          {author.image_url ? (
            <div className="aspect-square md:aspect-auto md:h-full bg-black/5">
              <img src={author.image_url} alt={author.name}
                   className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="aspect-square md:aspect-auto md:h-full grid place-items-center"
                 style={{ background: "var(--wofy-surface)" }}>
              <User className="w-20 h-20 opacity-30" />
            </div>
          )}
          <div className="p-6 sm:p-8 flex flex-col justify-center">
            <div className="overline mb-1" style={{ color: "#B25D38" }}>
              <Sparkles className="w-3 h-3 inline mr-1" /> POPULAR PEOPLE
            </div>
            <h1 className="font-display text-3xl sm:text-4xl leading-tight"
                data-testid="author-name">
              {author.name}
            </h1>
            {heroBio && (
              <p className="mt-2 text-base opacity-85 leading-relaxed" data-testid="author-hero-bio">
                {heroBio}
              </p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm opacity-80">
              <span data-testid="author-quote-count">
                {author.quote_count || (quotes?.length || 0)} quotes
              </span>
              {author.source === "wikipedia" && (
                <span className="inline-flex items-center gap-1 text-[11px] font-mono opacity-60">
                  <ShieldCheck className="w-3 h-3" /> Bio verified from Wikipedia
                </span>
              )}
            </div>
            {author.wikipedia_url && (
              <div className="mt-3">
                <a href={author.wikipedia_url} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border text-sm hover:shadow-md"
                   style={{ borderColor: "var(--wofy-border)" }}
                   data-testid="author-wiki">
                  <ExternalLink className="w-3.5 h-3.5" /> Read full biography on Wikipedia
                </a>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Full bio */}
      {author.bio && author.bio.length > (heroBio?.length || 0) + 40 && (
        <section className="wofy-card !p-6 mb-8" data-testid="author-bio-full">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} />
            <h2 className="font-display text-2xl">Biography</h2>
          </div>
          <p className="whitespace-pre-line opacity-90 leading-relaxed">{author.bio}</p>
        </section>
      )}

      {/* Quotes */}
      <section className="mb-10" data-testid="author-quotes">
        <div className="flex items-center gap-2 mb-3">
          <QuoteIcon className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} />
          <h2 className="font-display text-2xl">Quotes by {author.name}</h2>
        </div>
        {(quotes || []).length === 0 ? (
          <div className="wofy-card !p-6 opacity-70">No quotes on file yet.</div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {quotes.map((q) => (
              <li key={q.id}
                  className="rounded-2xl border p-4 hover:shadow-md transition"
                  style={{ borderColor: "var(--wofy-border)" }}
                  data-testid={`author-quote-${q.slug}`}>
                <QuoteIcon className="w-4 h-4 opacity-40" />
                <Link to={`/quotes/${q.slug}`} className="block mt-1 font-display text-lg leading-snug hover:underline">
                  "{q.text}"
                </Link>
                {q.category && (
                  <div className="text-[11px] font-mono uppercase tracking-wider opacity-60 mt-2">
                    {q.category}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Message board */}
      <section className="mb-16" data-testid="author-messages">
        <div className="flex items-center gap-2 mb-3">
          <MessagesSquare className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} />
          <h2 className="font-display text-2xl">Community message board</h2>
        </div>
        <p className="text-sm opacity-70 mb-4">
          Share what {author.name}'s work has meant to you. Be kind, be honest —
          this is a public guestbook.
        </p>

        <MessageComposer disabled={!user} onSubmit={postMessage} />

        {messagesLoading ? (
          <div className="inline-flex items-center gap-2 opacity-60 text-sm mt-3">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading messages…
          </div>
        ) : messages.length === 0 ? (
          <div className="wofy-card !p-6 opacity-70 text-sm mt-3">
            No messages yet — be the first.
          </div>
        ) : (
          <ul className="mt-4 space-y-3" data-testid="author-message-list">
            {messages.map((m) => (
              <li key={m.id}
                  className="rounded-2xl border p-3 flex items-start gap-3"
                  style={{ borderColor: "var(--wofy-border)" }}
                  data-testid={`author-msg-${m.id}`}>
                {m.user_avatar ? (
                  <img src={m.user_avatar} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-9 h-9 rounded-full grid place-items-center flex-shrink-0"
                       style={{ background: "var(--wofy-surface)" }}>
                    <User className="w-4 h-4 opacity-50" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs opacity-70">
                    <span className="font-medium">{m.user_name}</span>
                    {m.user_handle && <span>@{m.user_handle}</span>}
                    <span>·</span>
                    <span>{_ago(m.created_at)}</span>
                    {user?.id === m.user_id && (
                      <button type="button" onClick={() => deleteMessage(m.id)}
                              className="ml-auto opacity-60 hover:opacity-100"
                              aria-label="Delete"
                              data-testid={`author-msg-delete-${m.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="mt-1 opacity-90 whitespace-pre-line">{m.text}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}


function MessageComposer({ disabled, onSubmit }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (t.length < 2) return;
    setBusy(true);
    await onSubmit(t, () => setText(""));
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="rounded-2xl border p-3"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid="author-msg-composer">
      <textarea
        rows={3} maxLength={1500}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={disabled ? "Sign in to leave a message" : "Leave a message…"}
        disabled={disabled}
        className="w-full outline-none resize-y bg-transparent text-sm"
        data-testid="author-msg-input"
      />
      <div className="flex items-center justify-between mt-1">
        <div className="text-[11px] opacity-60">{text.length}/1500</div>
        <button type="submit" disabled={disabled || busy || text.trim().length < 2}
                className="wofy-btn-primary text-sm"
                data-testid="author-msg-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Post message
        </button>
      </div>
    </form>
  );
}
